import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";

import { useGetProjectQuery } from "../../../app/services/projects";
import { useGetCurrentUserQuery } from "../../../app/accounts/authuser";
import { useProjectFieldSync } from "../../../hooks/useProjectFieldSync";
import Calendar from "../../../components/calendar/Calendar";
import ListBlock from "../../../components/block/ListBlock";
import TitleBlock from "../../../components/block/TitleBlock";
import Input from "../../../components/form-item/Input";
import IconButton from "../../../components/button/IconButton";
import FieldListHeader from "../../../components/field-list/FieldListHeader";
import StatusIndicator from "../../../components/status-indicator/StatusIndicator";
import SvgLoadingAnimation from "../../../components/loading/index";

// 行の React key 用のローカル ID（並び替えで入力とDOMがずれないよう index key を避ける）
let milestoneUidSeq = 0;
const nextMilestoneUid = () => `ms-${++milestoneUidSeq}`;

/**
 * マイルストーンを期日昇順に並べる（期日未入力は末尾）。
 * Home の直近マイルストーン表示・ガントチャートと同じ昇順に揃える。
 */
const sortMilestonesByDate = (list) =>
  [...list].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });

const ProjectSchedule = () => {
  const { projectId } = useParams();
  const { data: currentUserData } = useGetCurrentUserQuery();
  const { data: projectData, isLoading } = useGetProjectQuery(
    { project_id: projectId },
    { skip: !projectId || !currentUserData }
  );
  const { updateStatus, debouncedSave } = useProjectFieldSync(projectId);

  const [period, setPeriod] = useState([null, null]);
  const [milestones, setMilestones] = useState([]);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!initializedRef.current && projectData?.data?.project) {
      const p = projectData.data.project;
      const start = p.start_date ? new Date(p.start_date) : null;
      const end = p.end_date ? new Date(p.end_date) : null;
      setPeriod([start, end]);

      const apiMilestones = (p.milestones || []).map((m) => ({
        uid: nextMilestoneUid(),
        event: m.event || "",
        date: m.date ? new Date(m.date) : "",
      }));
      setMilestones(sortMilestonesByDate(apiMilestones));
      initializedRef.current = true;
    }
  }, [projectData]);

  const saveMilestones = useCallback(
    (updated) => {
      const payload = updated
        .filter((m) => m.event || m.date)
        .map((m) => ({
          event: m.event,
          date: m.date ? new Date(m.date).toISOString() : "",
        }));
      debouncedSave({ milestones: payload });
    },
    [debouncedSave]
  );

  const handlePeriodChange = (e) => {
    const dates = e?.target?.value;
    const start = Array.isArray(dates) ? dates[0] ?? null : null;
    const end = Array.isArray(dates) && dates.length > 1 ? dates[1] ?? null : null;
    setPeriod([start, end]);
    debouncedSave({
      start_date: start ? new Date(start).toISOString() : null,
      end_date: end ? new Date(end).toISOString() : null,
    });
  };

  const handleMilestoneChange = (index, field, value) => {
    let next = [...milestones];
    next[index] = { ...next[index], [field]: value };
    // 期日の変更時のみ期日昇順に並べ直す（名称の入力中に行が動かないように）
    if (field === "date") {
      next = sortMilestonesByDate(next);
    }
    setMilestones(next);
    saveMilestones(next);
  };

  const handleAddMilestone = () => {
    // 期日未入力の新規行は末尾に置く（ソート順でも末尾のまま）
    const next = [...milestones, { uid: nextMilestoneUid(), event: "", date: "" }];
    setMilestones(next);
  };

  const handleDeleteMilestone = (index) => {
    const next = milestones.filter((_, i) => i !== index);
    setMilestones(next);
    saveMilestones(next);
  };

  if (isLoading) return <SvgLoadingAnimation />;

  return (
    <div className="u-col u-w100 u-gap16">
      <TitleBlock
        title="全体スケジュール"
        description="プロジェクトの期間と、節目となるマイルストーンを設定できます。"
      />
      <Calendar
        mode="range"
        label="期間"
        option="optional"
        placeholder="開始日 ~ 終了日"
        width="100%"
        value={period}
        onChange={handlePeriodChange}
        name="period"
      />
      <div className="u-w100 u-col u-gap8">
        {milestones.length > 0 && (
          <FieldListHeader
            columns={[
              { label: "期日", width: 224 },
              { label: "マイルストーン", flex: 1 },
            ]}
            actionWidth={24}
          />
        )}
        {milestones.map((ms, index) => (
          <div key={ms.uid} className="u-w100 u-row u-gap8">
            <div style={{ width: 224, flexShrink: 0 }}>
              <Calendar
                mode="single"
                width="100%"
                value={ms.date}
                onChange={(e) => handleMilestoneChange(index, "date", e.target?.value)}
                name={`milestone-${ms.uid}-date`}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                type="text"
                placeholder="マイルストーン"
                value={ms.event}
                onChange={(e) => handleMilestoneChange(index, "event", e.target.value)}
              />
            </div>
            <IconButton onClick={() => handleDeleteMilestone(index)} icon="delete" />
          </div>
        ))}
        <ListBlock type="add" label="マイルストーンを追加" onClick={handleAddMilestone} />
      </div>
      <StatusIndicator status={updateStatus} />
    </div>
  );
};

export default ProjectSchedule;
